import { Dialog } from '@angular/cdk/dialog';
import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, take } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { DashboardService } from 'src/app/services/dashboard.service';
import { MemberType } from '../../models/company.model';
import { LicenseBatchDto } from '../../models/license.model';
import { UserRole } from '../../models/user.model';
import {
  deleteMember,
  leaveCompany,
} from '../../stores/company/company.actions';
import {
  selectAllCompanyMembersWithSelfFirst,
  selectCompanyData,
} from '../../stores/company/company.selector';
import { updatingProfileRole } from '../../stores/profile/profile.actions';
import {
  selectProfileData,
  selectProfileRole,
} from '../../stores/profile/profile.selector';
import {
  CompanyState,
  LoadingState,
  ProfileState,
} from '../../stores/store.states';
import { dispatchLoadingMembers } from '../../stores/store.utils';
import {
  GroupActionsModalComponent,
  GroupActionsModalData,
} from '../modals/group-actions-modal/group-actions-modal.component';
import { InviteMemberModalComponent } from '../modals/invite-member-modal/invite-member-modal.component';

@Component({
  selector: 'app-members-table',
  templateUrl: './members-table.component.html',
  styleUrls: ['./members-table.component.scss'],
  standalone: false,
})
export class MembersTableComponent {
  profile$: Observable<ProfileState>;
  members$: Observable<MemberType[]>;
  memberTableData$: Observable<{
    members: MemberType[];
    showLicenseBatches: boolean;
  }>;
  company$: Observable<CompanyState>;
  role$ = this.store.select(selectProfileRole);

  constructor(
    private dialog: Dialog,
    private store: Store,
    private router: Router,
    private dashboardService: DashboardService,
    private translate: TranslateService,
  ) {
    dispatchLoadingMembers(store);
    this.profile$ = this.store.select(selectProfileData);
    this.members$ = this.store.select(selectAllCompanyMembersWithSelfFirst);
    this.memberTableData$ = this.members$.pipe(
      map((members) => ({
        members,
        showLicenseBatches: this.hasAnyLicenseBatches(members),
      })),
    );
    this.company$ = this.store.select(selectCompanyData);
  }

  openConfirmLeave(profileId: string) {
    this.dialog.open<GroupActionsModalData>(GroupActionsModalComponent, {
      data: {
        action: 'leave',
        leaving: true,
        buttonTitle: this.translate.instant('members.table.leave'),
        callback: (companyId: string) => {
          this.store.dispatch(
            leaveCompany({
              memberId: profileId,
              companyId: companyId,
            }),
          );
          this.dashboardService.openResponseBannerOnResponse(
            this.translate.instant('members.table.leave_success'),
            this.translate.instant('members.table.leave_error'),
            this.company$,
            'updateState',
          );
          this.router.navigate(['/profile/myprofile']);
        },
      },
    });
  }

  openInviteDialog() {
    const dialogRef = this.dialog.open<any>(InviteMemberModalComponent);
    dialogRef.closed.subscribe((result) => {
      console.log(result);
    });
  }

  editCompany() {
    this.router.navigate(['/profile/company']);
  }

  protected readonly UserRole = UserRole;

  changeRole(
    member: MemberType,
    company: CompanyState,
    isCurrentUser: boolean,
    newRole: UserRole,
  ) {
    this.store.dispatch(
      updatingProfileRole({
        memberId: member.userId,
        companyId: company.id as string,
        role: newRole,
      }),
    );
    if (isCurrentUser) {
      this.store
        .select(selectProfileData)
        .pipe(
          filter((profile) => profile.updatedState === LoadingState.SUCCESS),
          take(1),
        )
        .subscribe(() =>
          this.router
            .navigate(['/profile/company'])
            .then(() => window.location.reload()),
        );
    }
  }

  deleteMember(
    member: MemberType,
    company: CompanyState,
    isCurrentUser: boolean,
  ) {
    this.store.dispatch(
      deleteMember({
        memberId: member.userId,
        companyId: company.id as string,
      }),
    );
    if (isCurrentUser) {
      this.store
        .select(selectCompanyData)
        .pipe(
          filter((company) => company.updateState === LoadingState.SUCCESS),
          take(1),
        )
        .subscribe(() =>
          this.router
            .navigate(['/profile/myprofile'])
            .then(() => window.location.reload()),
        );
    }
  }

  protected readonly length = length;

  moreThanOneAdmin(members: MemberType[]) {
    return (
      members.filter((member) => member.role === UserRole.ADMIN).length > 1
    );
  }

  getRoleLabel(role: UserRole): string {
    switch (role) {
      case UserRole.PILOT:
        return this.translate.instant('members.table.pilot');
      case UserRole.ADMIN:
        return this.translate.instant('members.table.admin');
      case UserRole.MAINTAINER:
        return this.translate.instant('members.table.maintainer');
      default:
        return this.translate.instant('members.table.pilot');
    }
  }

  getLicenseBatches(member: MemberType): LicenseBatchDto[] {
    const seenIds = new Set<string>();
    return (member.licenseBatches ?? []).filter((batch) => {
      const batchId = batch.id?.trim();
      if (!batchId || seenIds.has(batchId)) {
        return false;
      }
      seenIds.add(batchId);
      return true;
    });
  }

  private hasAnyLicenseBatches(members: MemberType[]): boolean {
    return members.some((member) => this.getLicenseBatches(member).length > 0);
  }

  getLicenseBatchLabel(batch: LicenseBatchDto): string {
    return batch.name?.trim() || batch.id?.trim() || 'License Batch';
  }

  trackLicenseBatch(index: number, batch: LicenseBatchDto): string {
    return batch.id ?? `${batch.name ?? 'license-batch'}-${index}`;
  }
}
